import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_property-dispatch/artifacts/zkteztq8_OrchardLMS%20Logo.png";
const QUIDITY_LOGO_URL = "https://customer-assets.emergentagent.com/job_property-dispatch/artifacts/iwc7hwta_QuidityDirect%20Logo.png";

const MarketingLayout = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const featureLinks = [
    { path: '/features/ai-assistant', label: 'AI Assistant', primary: true },
    { path: '/features/property-management', label: 'Property Management' },
    { path: '/features/scheduling', label: 'Scheduling & Routes' },
    { path: '/features/crew-management', label: 'Crew Management' },
    { path: '/features/estimates-invoicing', label: 'Estimates & Invoicing' },
    { path: '/features/reporting', label: 'Reporting & Analytics' },
    { path: '/features/mobile', label: 'Mobile App' },
  ];

  const financingLinks = [
    { path: '/financing/working-capital', label: 'Working Capital Line of Credit' },
    { path: '/financing/term-loans', label: 'Term Loans' },
    { path: '/financing/equipment', label: 'Equipment Financing' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Orchard LMS" className="h-10 w-auto" />
            </Link>
            
            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-8">
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-primary transition-colors">
                  Features <ChevronDown className="h-4 w-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <DropdownMenuItem asChild>
                    <Link to="/features" className="w-full">All Features</Link>
                  </DropdownMenuItem>
                  <div className="h-px bg-gray-100 my-1" />
                  {featureLinks.map((link) => (
                    <DropdownMenuItem key={link.path} asChild>
                      <Link 
                        to={link.path} 
                        className={`w-full flex items-center gap-2 ${link.primary ? 'text-violet-600 font-medium' : ''}`}
                      >
                        {link.primary && <span className="w-2 h-2 bg-violet-500 rounded-full"></span>}
                        {link.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-primary transition-colors">
                  Business Financing <ChevronDown className="h-4 w-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64">
                  <DropdownMenuItem asChild>
                    <Link to="/financing" className="w-full">Overview</Link>
                  </DropdownMenuItem>
                  <div className="h-px bg-gray-100 my-1" />
                  {financingLinks.map((link) => (
                    <DropdownMenuItem key={link.path} asChild>
                      <Link to={link.path} className="w-full">{link.label}</Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Link to="/pricing" className="text-sm font-medium text-gray-600 hover:text-primary transition-colors">Pricing</Link>
              <Link to="/about" className="text-sm font-medium text-gray-600 hover:text-primary transition-colors">About</Link>
              <Link to="/contact" className="text-sm font-medium text-gray-600 hover:text-primary transition-colors">Contact</Link>
              <Link to="/login" className="text-sm font-medium text-gray-600 hover:text-primary transition-colors">Login</Link>
              <Link to="/signup">
                <Button data-testid="header-signup-btn" className="bg-primary hover:bg-primary/90">
                  Get Started Free
                </Button>
              </Link>
            </nav>

            {/* Mobile menu button */}
            <button 
              className="lg:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 py-4">
            <div className="max-w-7xl mx-auto px-4 space-y-4">
              <Link to="/features" className="block text-sm font-medium text-gray-600" onClick={() => setMobileMenuOpen(false)}>Features</Link>
              <Link to="/financing" className="block text-sm font-medium text-gray-600" onClick={() => setMobileMenuOpen(false)}>Business Financing</Link>
              <Link to="/pricing" className="block text-sm font-medium text-gray-600" onClick={() => setMobileMenuOpen(false)}>Pricing</Link>
              <Link to="/about" className="block text-sm font-medium text-gray-600" onClick={() => setMobileMenuOpen(false)}>About</Link>
              <Link to="/contact" className="block text-sm font-medium text-gray-600" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
              <Link to="/login" className="block text-sm font-medium text-gray-600" onClick={() => setMobileMenuOpen(false)}>Login</Link>
              <Link to="/signup" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full bg-primary hover:bg-primary/90">Get Started Free</Button>
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="pt-16">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-8">
            <div className="lg:col-span-2">
              <img src={LOGO_URL} alt="Orchard LMS" className="h-10 w-auto mb-4 brightness-0 invert" />
              <div className="flex items-center gap-2 mb-4 bg-white/10 rounded-lg px-3 py-2 w-fit">
                <span className="text-sm text-gray-300">by</span>
                <img src={QUIDITY_LOGO_URL} alt="Quidity Direct" className="h-8 w-auto brightness-0 invert" />
              </div>
              <p className="text-gray-400 text-sm max-w-sm">
                The complete operating system for landscaping companies. Manage properties, optimize routes, schedule crews, and grow your business.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/features" className="hover:text-white transition-colors">All Features</Link></li>
                <li><Link to="/features/property-management" className="hover:text-white transition-colors">Property Management</Link></li>
                <li><Link to="/features/scheduling" className="hover:text-white transition-colors">Scheduling & Routes</Link></li>
                <li><Link to="/features/mobile" className="hover:text-white transition-colors">Mobile App</Link></li>
                <li><Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Financing</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/financing" className="hover:text-white transition-colors">Overview</Link></li>
                <li><Link to="/financing/working-capital" className="hover:text-white transition-colors">Working Capital</Link></li>
                <li><Link to="/financing/term-loans" className="hover:text-white transition-colors">Term Loans</Link></li>
                <li><Link to="/financing/equipment" className="hover:text-white transition-colors">Equipment Financing</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Documentation</a></li>
                <li><a href="#" className="hover:text-white transition-colors">API Reference</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Status</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center gap-4">
              <p className="text-sm text-gray-400">&copy; 2025 Orchard LMS. All rights reserved.</p>
              <span className="text-gray-600">|</span>
              <div className="flex items-center gap-2 bg-white/10 rounded px-2 py-1">
                <span className="text-xs text-gray-300">Powered by</span>
                <img src={QUIDITY_LOGO_URL} alt="Quidity Direct" className="h-4 w-auto brightness-0 invert" />
              </div>
            </div>
            <div className="flex gap-6 mt-4 md:mt-0">
              <a href="#" className="text-sm text-gray-400 hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="text-sm text-gray-400 hover:text-white transition-colors">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MarketingLayout;
